import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Dialog from '../../organisms/Dialog';
import SelectTextField from '../../molecules/SelectTextField';
import { monitoringApi, CreateMonitoringDTO, MonitoringApiError } from '../../../services/monitoringApi';
import { personApi } from '../../../services/personApi';

interface MonitoringCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

type RiskLevel = 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';

const RISK_LEVELS: { value: RiskLevel; label: string }[] = [
    { value: 'EXTREME', label: 'Extremo' },
    { value: 'HIGH', label: 'Alto' },
    { value: 'MEDIUM', label: 'Médio' },
    { value: 'LOW', label: 'Baixo' },
];

const MonitoringCreateModal: React.FC<MonitoringCreateModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [taxId, setTaxId] = useState('');
    const [personId, setPersonId] = useState('');
    const [personName, setPersonName] = useState('');
    const [personFound, setPersonFound] = useState(false);
    const [riskLevel, setRiskLevel] = useState<RiskLevel | ''>('');
    const [notes, setNotes] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [verified, setVerified] = useState(false);
    const [apiError, setApiError] = useState('');

    const resetForm = () => {
        setTaxId('');
        setPersonId('');
        setPersonName('');
        setPersonFound(false);
        setRiskLevel('');
        setNotes('');
        setVerified(false);
        setApiError('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const cleanDocument = (value: string) => value.replace(/[.\-/\s]/g, '').toUpperCase();

    const isDocumentValid = () => {
        const clean = cleanDocument(taxId);
        // CPF: 11 digits | CNPJ: 14 alphanumeric chars (supports new format)
        return clean.length === 11 || clean.length === 14;
    };

    const handleVerify = async () => {
        const clean = cleanDocument(taxId);
        if (!isDocumentValid()) {
            toast.warn('Documento deve ter 11 (CPF) ou 14 (CNPJ) dígitos');
            return;
        }

        setIsVerifying(true);
        setApiError('');
        setPersonFound(false);
        try {
            // CPF: 11 digits only | CNPJ: 14 chars (numeric or alphanumeric)
            const isCpf = clean.length === 11 && /^\d+$/.test(clean);

            if (isCpf) {
                // CPF — buscar pessoa física
                const person = await personApi.searchIndividualByCpf(clean);
                if (person) {
                    setPersonId(person.uuid);
                    setPersonName(person.name);
                    setPersonFound(true);
                    setVerified(true);
                } else {
                    setPersonId('');
                    setPersonName('');
                    setVerified(true);
                    toast.info('Documento não encontrado no cadastro. Preencha o nome manualmente.');
                }
            } else {
                // CNPJ — buscar pessoa jurídica
                const person = await personApi.searchLegalPersonByCnpj(clean);
                if (person) {
                    setPersonId(person.personId);
                    setPersonName(person.legalName);
                    setPersonFound(true);
                    setVerified(true);
                } else {
                    setPersonId('');
                    setPersonName('');
                    setVerified(true);
                    toast.info('Documento não encontrado no cadastro. Preencha a razão social manualmente.');
                }
            }
        } catch {
            setVerified(true);
            setPersonId('');
            setPersonName('');
            toast.info('Não foi possível verificar o documento. Preencha os dados manualmente.');
        } finally {
            setIsVerifying(false);
        }
    };

    const canSubmit = () => {
        const clean = cleanDocument(taxId);
        return (
            clean.length >= 11 && clean.length <= 14 &&
            personName.trim().length > 0 &&
            riskLevel !== '' &&
            !isSubmitting
        );
    };

    const handleSubmit = async () => {
        if (!canSubmit()) return;

        setIsSubmitting(true);
        setApiError('');
        try {
            const dto: CreateMonitoringDTO = {
                person_id: personId || cleanDocument(taxId),
                tax_id: cleanDocument(taxId),
                person_name: personName.trim(),
                risk_level: riskLevel as RiskLevel,
                notes: notes.trim() || undefined,
            };
            await monitoringApi.create(dto);
            toast.success('Documento cadastrado para monitoramento');
            onSuccess();
            handleClose();
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                setApiError(e.message);
            } else {
                setApiError('Erro inesperado ao cadastrar. Tente novamente.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const body = (
        <div className="p-4 space-y-4">
            {/* Documento */}
            <div>
                <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 mb-1">
                    Documento (CPF/CNPJ) *
                </label>
                <div className="flex gap-2">
                    <input
                        id="taxId"
                        type="text"
                        value={taxId}
                        onChange={(e) => { setTaxId(e.target.value); setVerified(false); setPersonFound(false); }}
                        onKeyUp={(e) => { if (e.key === 'Enter') handleVerify(); }}
                        placeholder="Digite CPF ou CNPJ"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                        maxLength={18}
                    />
                    <button
                        onClick={handleVerify}
                        disabled={!isDocumentValid() || isVerifying}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                        {isVerifying ? 'Verificando...' : 'Verificar'}
                    </button>
                </div>
            </div>

            {/* Dados encontrados */}
            {verified && personFound && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-800">
                        Cadastro encontrado: <span className="font-medium">{personName}</span>
                    </p>
                </div>
            )}

            {/* Nome */}
            <div>
                <label htmlFor="personName" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome / Razão Social *
                </label>
                <input
                    id="personName"
                    type="text"
                    value={personName}
                    onChange={(e) => setPersonName(e.target.value)}
                    placeholder="Nome completo ou razão social"
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500 ${personFound ? 'bg-gray-100 dark:bg-slate-600 cursor-not-allowed' : 'bg-white dark:bg-slate-700'}`}
                    maxLength={150}
                    disabled={personFound}
                />
                {personFound && (
                    <p className="text-xs text-gray-500 mt-1">Preenchido automaticamente a partir do cadastro.</p>
                )}
            </div>

            {/* Nível de Risco */}
            <SelectTextField
                id="riskLevel"
                name="riskLevel"
                label="Nível de Risco *"
                value={riskLevel}
                onChange={(e) => setRiskLevel(e.target.value as RiskLevel | '')}
                items={RISK_LEVELS.map(r => ({ value: r.value, label: r.label }))}
                emptyLabel="Selecione o nível de risco"
            />

            {/* Observação */}
            <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Observação
                </label>
                <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Motivo do cadastro (opcional)"
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
                <p className="text-xs text-gray-500 text-right mt-1">
                    {notes.length}/500
                </p>
            </div>

            {/* Erro da API */}
            {apiError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{apiError}</p>
                </div>
            )}
        </div>
    );

    const footer = (
        <div className="flex gap-3">
            <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
                Cancelar
            </button>
            <button
                onClick={handleSubmit}
                disabled={!canSubmit()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isSubmitting ? 'Cadastrando...' : 'Cadastrar'}
            </button>
        </div>
    );

    return (
        <Dialog
            id="monitoring-create-modal"
            show={isOpen}
            onClose={handleClose}
            header="Cadastrar Documento para Monitoramento"
            body={body}
            footer={footer}
            size="md"
        />
    );
};

export default MonitoringCreateModal;
